import type { ITermBreakdown, TEnterableTerm } from '~/types/msService/models'

/**
 * Count the terms of `msServices`. The CALLER picks the set: a service group
 * passes exactly its # Srvs set (getCountableMSServices), so the hover's counts
 * add up to that column.
 *
 * A tie for most common goes to `preferredTerm` -- the service group's
 * defaultTerm, the term the group is meant to have -- when it is one of the
 * tied terms, and otherwise to the LONGER term. A default that is not among
 * them is ignored: it would show a term no service has. Only opportunity
 * service groups have a default.
 *
 * Replaces a compare-with-the-first-row check, which read 'Varies' whenever the
 * first row had no term, even when every other row agreed.
 */
export function getTermBreakdown(
  msServices: { term?: TEnterableTerm | null }[],
  { preferredTerm }: { preferredTerm?: TEnterableTerm | null } = {}
): ITermBreakdown {
  const countByTerm = new Map<TEnterableTerm, number>()
  for (const msService of msServices) {
    const term: TEnterableTerm =
      typeof msService.term === 'number' ? msService.term : '--'
    countByTerm.set(term, (countByTerm.get(term) ?? 0) + 1)
  }
  const counts = Array.from(countByTerm, ([term, count]) => ({ term, count }))
  counts.sort((a, b) => {
    if (a.term === '--' || b.term === '--') {
      return a.term === '--' ? 1 : -1
    }
    return (
      b.count - a.count ||
      Number(b.term === preferredTerm) - Number(a.term === preferredTerm) ||
      b.term - a.term
    )
  })
  const realTerms = counts.filter((c) => c.term !== '--')
  const majorityTerm = realTerms[0]?.term ?? '--'
  const doesVary = realTerms.length > 1
  return {
    term: doesVary ? 'Varies' : majorityTerm,
    majorityTerm,
    counts,
    doesVary,
  }
}

/**
 * A Term column's cell value: the majority term where the msServices' terms
 * vary, otherwise the row's own term. TableCard sorts on this too, so a varied
 * row sorts with its number rather than as 'Varies'.
 */
export function getDisplayTerm(item: {
  term?: TEnterableTerm | 'Varies' | null
  termBreakdown?: ITermBreakdown
}) {
  return item.termBreakdown?.majorityTerm ?? item.term
}

/**
 * A varied row's Term cell: the majority term marked "+" ("24+"), so it is not
 * read as every service's term. Only the text: the column sorts on the number.
 */
export function formatVaryingTerm(majorityTerm: unknown) {
  return `${majorityTerm}+`
}

/** Only a row whose terms vary gets the Term hover. */
export function hasVaryingTerm(item: { termBreakdown?: ITermBreakdown }) {
  return Boolean(item.termBreakdown?.doesVary)
}

/** The Term hover's rows: "3 services" / "24 months", "no term" last. */
export function getTermBreakdownRows(termBreakdown?: ITermBreakdown) {
  return (termBreakdown?.counts ?? []).map(({ term, count }) => ({
    count: `${count} service${count === 1 ? '' : 's'}`,
    label: term === '--' ? 'no term' : `${term} month${term === 1 ? '' : 's'}`,
  }))
}
